/**
 * Zod input schemas: announcements. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
import { ISODateSchema } from './absence.js';
/** Announcements (admin-authored, Home). */
export const AnnouncementAudienceSchema = z.object({
    all: z.boolean(),
    entities: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
    teams: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
    /** Tool slugs: people with that app (Oct 2026). */
    tools: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    /** Specific people (Oct 2026). */
    emails: z.array(z.string().trim().toLowerCase().email()).max(300).default([]),
});
const audienceSet = (a) => a.all || a.entities.length > 0 || a.teams.length > 0 || (a.tools ?? []).length > 0 || (a.emails ?? []).length > 0;
/** Images must be ones the hub stored (announcementImageUpload), not arbitrary URLs. */
const AnnouncementImageUrl = z.string().trim().max(600)
    .regex(/^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/announcements%2Fimages%2F[^?]+\?alt=media&token=[0-9a-f-]+$/, 'Upload images through the editor.');
export const AnnouncementBlockSchema = z.object({
    id: z.string().trim().min(1).max(40),
    body: z.string().trim().max(4000),
    imageUrl: AnnouncementImageUrl.nullable().optional(),
    /** null = everyone who receives the announcement. */
    audience: AnnouncementAudienceSchema.nullable().optional(),
});
export const AnnouncementSaveSchema = z
    .object({
    id: z.string().trim().min(1).max(64).optional(),
    title: z.string().trim().min(1).max(140),
    /** Older single-text posts; since Oct 2026 the content comes in `blocks`. */
    body: z.string().trim().max(4000).optional(),
    blocks: z.array(AnnouncementBlockSchema).min(1).max(8).optional(),
    channels: z.object({ home: z.boolean(), popup: z.boolean(), email: z.boolean() }).optional(),
    publishTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().optional(),
    linkUrl: z.string().trim().url().max(500).nullable().optional(),
    linkLabel: z.string().trim().max(60).nullable().optional(),
    audience: AnnouncementAudienceSchema,
    publishAt: ISODateSchema,
    expiresAt: ISODateSchema,
    status: z.enum(['draft', 'published', 'template']),
})
    .refine((v) => v.expiresAt >= v.publishAt, { message: 'The disappear date must be on or after the publish date.' })
    .refine((v) => audienceSet(v.audience), { message: 'Pick an audience: everyone, or at least one entity, team, app or person.' })
    .refine((v) => (v.blocks?.length ? v.blocks.some((b) => b.body.trim() || b.imageUrl) : !!v.body?.trim()), { message: 'Write some text (or add an image) first.' })
    .refine((v) => (v.blocks ?? []).every((b) => !b.audience || audienceSet(b.audience)), { message: 'A message block with its own audience needs at least one entity, team or app.' })
    .refine((v) => !v.channels || v.channels.home || v.channels.popup || v.channels.email, { message: 'Choose at least one channel: Home, pop-up or email.' });
export const AnnouncementDeleteSchema = z.object({ id: z.string().trim().min(1).max(64) });
/** Preview / test email of an unsaved announcement, rendered for one person. */
export const AnnouncementPreviewSchema = z.object({
    draft: z.object({ title: z.string().max(140), blocks: z.array(AnnouncementBlockSchema).max(8), audience: AnnouncementAudienceSchema,
        linkUrl: z.string().trim().max(500).nullable().optional(), linkLabel: z.string().trim().max(60).nullable().optional() }),
    personEmail: z.string().trim().toLowerCase().email(),
    sendTestEmail: z.boolean().optional(),
});
export const AnnouncementImageUploadSchema = z.object({ base64: z.string().min(10).max(6_000_000) });
//# sourceMappingURL=announcements.js.map